import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { dealsApi, leadsApi, propertiesApi } from '../api/endpoints';
import { useAuth } from '../context/AuthContext';
import { DeleteButton } from '../components/DeleteButton';
import { toast } from 'sonner';
import { useTeamOptions } from '../hooks/useTeamOptions';

const stages = ['Qualified', 'Site Visit', 'Negotiation', 'Booked'];
const stageMeta = {
  Qualified:    { color: 'border-t-blue-500',    badge: 'bg-blue-50 text-blue-700' },
  'Site Visit': { color: 'border-t-amber-500',   badge: 'bg-amber-50 text-amber-700' },
  Negotiation:  { color: 'border-t-violet-500',  badge: 'bg-violet-50 text-violet-700' },
  Booked:       { color: 'border-t-emerald-500', badge: 'bg-emerald-50 text-emerald-700' },
};
const probByStage = { Qualified: 40, 'Site Visit': 60, Negotiation: 80, Booked: 100 };

const formatINR = (n) => {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)} L`;
  return `₹${(n || 0).toLocaleString()}`;
};

const DealFormDialog = ({ leads, properties, onSaved, trigger, initial }) => {
  const isEdit = !!initial;
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '', lead: '', property: '', value: '', stage: 'Qualified',
    closeDate: '', owner: '',
  });
  const team = useTeamOptions();

  useEffect(() => {
    if (open) {
      setForm({
        title: initial?.title || '',
        lead: initial?.lead || (leads[0]?.name || ''),
        property: initial?.property || (properties[0]?.name || ''),
        value: initial?.value ? String(initial.value) : '',
        stage: initial?.stage || 'Qualified',
        closeDate: initial?.closeDate || '',
        owner: initial?.owner || '',
      });
    }
    // eslint-disable-next-line
  }, [open, initial]);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!isEdit && form.property && form.lead && !form.title.trim()) {
      update('title', `${form.property} - ${form.lead}`);
    }
    // eslint-disable-next-line
  }, [form.property, form.lead]);

  const submit = async () => {
    if (!form.title || !form.value) { toast.error('Title and value are required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, value: parseInt(form.value, 10) || 0, probability: probByStage[form.stage] };
      const saved = isEdit
        ? await dealsApi.update(initial.id, payload)
        : await dealsApi.create(payload);
      onSaved(saved, isEdit);
      toast.success(isEdit ? 'Deal updated' : 'Deal created successfully');
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save deal');
    } finally { setSaving(false); }
  };

  const uniqueLeads = [...new Map(leads.map(l => [l.name, l])).values()];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{isEdit ? 'Edit Deal' : 'Create New Deal'}</DialogTitle>
          <DialogDescription>{isEdit ? 'Update deal details and stage.' : 'Add a deal to the Kiana sales pipeline.'}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><Label>Deal Title *</Label><Input value={form.title} onChange={e => update('title', e.target.value)} /></div>
          <div>
            <Label>Lead</Label>
            <Select value={form.lead} onValueChange={v => update('lead', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{uniqueLeads.map(l => <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Project</Label>
            <Select value={form.property} onValueChange={v => update('property', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{properties.map(p => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Deal Value (₹) *</Label><Input type="number" value={form.value} onChange={e => update('value', e.target.value)} placeholder="e.g. 9500000" /></div>
          <div>
            <Label>Stage</Label>
            <Select value={form.stage} onValueChange={v => update('stage', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{stages.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Expected Close Date</Label><Input type="date" value={form.closeDate} onChange={e => update('closeDate', e.target.value)} /></div>
          <div>
            <Label>Owner</Label>
            <Select value={form.owner} onValueChange={v => update('owner', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{team.map(u => <SelectItem key={u.id} value={u.name}>{u.name} — {u.role}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button className="bg-brand-navy hover:bg-[#13294A]" onClick={submit} disabled={saving}>{saving ? 'Saving...' : (isEdit ? 'Save Changes' : 'Create Deal')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Deals = () => {
  const { hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const canCreate = hasPermission('deals', 'create');
  const canEdit = hasPermission('deals', 'edit');
  const canDelete = hasPermission('deals', 'delete');

  const [data, setData] = useState([]);
  const [leads, setLeads] = useState([]);
  const [props, setProps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragId, setDragId] = useState(null);
  
  // Search & Routing states
  const [q, setQ] = useState('');
  const [targetStageFilter, setTargetStageFilter] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [d, l, p] = await Promise.all([
          dealsApi.list(),
          leadsApi.list().catch(() => []),
          propertiesApi.list().catch(() => []),
        ]);
        setData(d); setLeads(l); setProps(p);
      } catch (e) {
        console.error('Failed to load deals', e);
        toast.error('Failed to load deals');
      } finally { setLoading(false); }
    })();
  }, []);

  // Intercept routing payload for future dashboard wiring
  useEffect(() => {
    if (location.state?.filterStage) {
      setTargetStageFilter(location.state.filterStage);
    }
  }, [location.state]);

  // Compute filtered pipeline view dynamically
  const filteredData = useMemo(() => {
    let result = data;
    
    // Route state filtering
    if (targetStageFilter) {
      result = result.filter(d => d.stage === targetStageFilter);
    }

    // Text search filtering
    if (q.trim()) {
      const query = q.toLowerCase();
      result = result.filter(d => 
        [d.title, d.lead, d.property, d.owner].some(field => (field || '').toLowerCase().includes(query))
      );
    }
    
    return result;
  }, [data, q, targetStageFilter]);

  const onDrop = async (stage) => {
    if (!dragId || !canEdit) return;
    const id = dragId; 
    setDragId(null);
    const prev = data;
    setData(d => d.map(x => x.id === id ? { ...x, stage, probability: probByStage[stage] } : x));
    
    try {
      await dealsApi.update(id, { stage, probability: probByStage[stage] });
      toast.success(`Deal moved to ${stage}`);
    } catch (e) {
      setData(prev); // Revert UI on failure
      toast.error('Failed to update deal stage');
    }
  };

  const onSaved = (saved, isEdit) => {
    if (isEdit) setData(d => d.map(x => x.id === saved.id ? saved : x));
    else setData(prev => [saved, ...prev]);
  };

  const onDelete = async (d) => {
    await dealsApi.remove(d.id);
    setData(prev => prev.filter(x => x.id !== d.id));
    toast.success('Deal removed');
  };
  
  const clearFilters = () => {
    setTargetStageFilter(null);
    setQ('');
    navigate('.', { replace: true, state: null });
  };

  // Pipeline aggregate calculations
  const total = filteredData.reduce((s, d) => s + (d.value || 0), 0);

  return (
    <div className="space-y-5">
      
      {/* Route Filter Banner */}
      {targetStageFilter && (
        <div className="p-3 bg-blue-50 border border-blue-200 text-blue-800 rounded-lg flex items-center justify-between text-xs font-medium animate-in fade-in duration-300">
          <div className="flex items-center gap-2">
            <Icons.Filter className="w-4 h-4 text-blue-600 shrink-0" />
            <span>Showing deals exclusively in the <strong>{targetStageFilter}</strong> stage.</span>
          </div>
          <Button variant="ghost" size="sm" onClick={clearFilters} className="h-7 text-blue-700 hover:bg-blue-100 uppercase tracking-wider text-[10px]">
            Clear Filter
          </Button>
        </div>
      )}

      {/* Toolbar & Pipeline Metrics */}
      <Card className="border-slate-200">
        <CardContent className="p-3 sm:p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="w-full md:w-auto flex flex-col md:flex-row justify-between gap-4">
            <div>
              <div className="text-[11px] text-slate-500 uppercase tracking-wider">Pipeline Value</div>
              <div className="font-display text-xl sm:text-2xl font-bold text-brand-navy">{formatINR(total)}</div>
            </div>
            <div className="hidden lg:flex flex-wrap gap-4 ml-6 pl-6 border-l border-slate-100">
              {stages.map(s => {
                const v = filteredData.filter(d => d.stage === s).reduce((sum, d) => sum + (d.value || 0), 0);
                const c = filteredData.filter(d => d.stage === s).length;
                return (
                  <div key={s} className="text-right">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500">{s}</div>
                    <div className="text-sm font-bold text-slate-800">{formatINR(v)} <span className="text-slate-400 text-xs font-normal">({c})</span></div>
                  </div>
                );
              })}
            </div>
          </div>
          
          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="relative flex-1 md:w-64">
              <Icons.Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input 
                className="pl-9" 
                placeholder="Search deals..." 
                value={q} 
                onChange={e => setQ(e.target.value)} 
              />
            </div>
            {canCreate && (
              <DealFormDialog
                leads={leads} properties={props}
                onSaved={onSaved}
                trigger={
                  <Button className="bg-brand-navy hover:bg-[#13294A] text-white shrink-0">
                    <Icons.Plus className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">New Deal</span>
                  </Button>
                }
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Kanban Board */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
        {stages.map(stage => {
          const columnData = filteredData.filter(d => d.stage === stage);
          return (
            <div
              key={stage}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(stage)}
              className={`rounded-lg bg-slate-50 border-t-4 ${stageMeta[stage].color} border border-slate-200 p-3 min-h-[300px] flex flex-col`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="font-semibold text-slate-800">{stage}</div>
                <Badge variant="outline" className="text-[10px] bg-white">{columnData.length}</Badge>
              </div>
              
              <div className="space-y-3 flex-1">
                {loading && Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-28" />)}
                
                {!loading && columnData.length === 0 && (
                  <div className="h-full min-h-[100px] border-2 border-dashed border-slate-200 rounded flex flex-col items-center justify-center text-slate-400 p-4 text-center">
                    <span className="text-xs font-medium">Drop deal here</span>
                  </div>
                )}

                {!loading && columnData.map(d => (
                  <Card
                    key={d.id}
                    draggable={canEdit}
                    onDragStart={() => setDragId(d.id)}
                    className={`border-slate-200 card-lift ${canEdit ? 'cursor-move hover:border-slate-300' : ''}`}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-[10px] text-brand-gold font-semibold uppercase tracking-wider">{d.id}</div>
                          <div className="font-medium text-sm text-slate-900 truncate" title={d.title}>{d.title}</div>
                        </div>
                        <div className={`text-[10px] px-1.5 py-0.5 rounded ${stageMeta[stage].badge} font-medium`}>{d.probability}%</div>
                      </div>
                      <div className="mt-2 font-display text-lg font-bold text-brand-navy">{formatINR(d.value)}</div>
                      <div className="text-[11px] text-slate-500 truncate" title={d.property}>{d.property}</div>
                      <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500 pt-2 border-t border-slate-100">
                        <span className="flex items-center gap-1 truncate"><Icons.User className="w-3 h-3 shrink-0" /> {(d.owner || '').split(' ')[0]}</span>
                        <div className="flex items-center gap-0.5">
                          {canEdit && (
                            <DealFormDialog
                              leads={leads} properties={props}
                              initial={d}
                              onSaved={onSaved}
                              trigger={<Button size="icon" variant="ghost" className="h-6 w-6" title="Edit"><Icons.Pencil className="w-3 h-3" /></Button>}
                            />
                          )}
                          {canDelete && (
                            <DeleteButton title={`Delete deal ${d.id}?`} description="This will remove the deal from the pipeline." onConfirm={() => onDelete(d)} />
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Deals;